import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { LandingSections } from "@/components/landing/sections";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import { Logo } from "@/components/ui/Logo";
import { hasUserCookie } from "@/lib/auth-cookie";
import { getIcon } from "@/lib/icon-map";
import { brand } from "@/theme/themes";

export const Route = createFileRoute("/")({
	beforeLoad: () => {
		// Bewusst ohne ensureSessionMarker (siehe _auth.tsx): die Landing-Page ist
		// komplett auth-frei und darf nicht auf einen Backend-Roundtrip warten.
		if (hasUserCookie()) {
			throw redirect({ to: "/search" });
		}
	},
	component: LandingPage,
});

function LandingPage() {
	const { hero, landing } = brand;

	// Config-getriebene Landing: Ist `landing.sections` gesetzt, rendert das Theme
	// ausschliesslich diese Sections. Andernfalls greift die klassische Struktur
	// (Backwards-Compat fuer Themes ohne `sections`).
	if (landing.sections && landing.sections.length > 0) {
		return (
			<div className="min-h-screen bg-background">
				<LandingSections sections={landing.sections} />
				<Footer minimal />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-background">
			<HeroSection hero={hero} />
			{landing.benefits && <BenefitsSection benefits={landing.benefits} />}
			{landing.quote && <QuoteSection quote={landing.quote} />}
			{landing.cta && <CtaSection cta={landing.cta} />}
			<Footer minimal />
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */

function HeroSection({ hero }: { hero: typeof brand.hero }) {
	return (
		<section className="relative min-h-[90vh] flex items-center justify-center pt-[12vh] sm:pt-0 bg-background overflow-x-hidden -mb-1">
			{/* Background Image */}
			{hero.image && (
				<img
					src={hero.image}
					alt=""
					aria-hidden="true"
					width={1920}
					height={1080}
					loading="eager"
					className="absolute inset-0 w-full h-full object-cover"
				/>
			)}

			{/* Overlay: subtle primary tint at top, fading to white/background at bottom */}
			<div
				className="absolute inset-0"
				style={{
					background: [
						"linear-gradient(to bottom,",
						"color-mix(in srgb, var(--color-primary) 8%, transparent) 0%,",
						"color-mix(in srgb, var(--color-primary) 5%, transparent) 20%,",
						"rgba(255,255,255,0.85) 45%,",
						"var(--color-background) 65%)",
					].join(" "),
				}}
			/>

			{/* Hero Content */}
			<div className="relative z-10 text-center px-5 sm:px-8 max-w-3xl mx-auto">
				{/* Logo pulse */}
				<div className="mb-7 animate-fade-up">
					<Logo size={72} className="mx-auto animate-heart-pulse" />
				</div>

				{/* Headline */}
				<h1 className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight mb-3 animate-fade-up-delay-1">
					<span className="text-text">{hero.title}</span>
					{hero.titleAccent && (
						<>
							{" "}
							<span className="text-primary italic">{hero.titleAccent}</span>
						</>
					)}
				</h1>

				{/* Subtitle */}
				<p className="text-lg sm:text-xl text-text-secondary leading-relaxed max-w-xl mx-auto mb-10 animate-fade-up-delay-2 font-light">
					{hero.subtitle}
				</p>

				{/* CTAs */}
				<div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up-delay-3">
					<Link to="/register" className="no-underline">
						<Button
							variant="primary"
							size="lg"
							className="text-base px-8 py-3 shadow-md hover:shadow-lg transition-shadow w-full sm:w-auto font-semibold"
						>
							Jetzt registrieren
						</Button>
					</Link>
					<Link to="/login" className="no-underline">
						<Button
							variant="outline"
							size="lg"
							className="text-base px-8 py-3 w-full sm:w-auto border-2 border-primary text-primary hover:bg-primary/5"
						>
							Einloggen
						</Button>
					</Link>
				</div>
			</div>
		</section>
	);
}

/* ------------------------------------------------------------------ */
/*  Benefits                                                           */
/* ------------------------------------------------------------------ */

function BenefitsSection({ benefits }: { benefits: NonNullable<typeof brand.landing.benefits> }) {
	return (
		<section
			className="relative z-10 py-20 sm:py-24 px-5 sm:px-8"
			style={{
				backgroundColor: "var(--color-background)",
				background: [
					"linear-gradient(180deg,",
					"var(--color-background) 0%,",
					"color-mix(in srgb, var(--color-primary) 4%, var(--color-background)) 50%,",
					"var(--color-background) 100%)",
				].join(" "),
			}}
		>
			<div className="max-w-5xl mx-auto">
				<h2 className="text-3xl md:text-4xl font-bold text-center mb-3 text-text">
					{benefits.title}
				</h2>
				<p className="text-text-secondary text-center mb-14 max-w-lg mx-auto">
					{benefits.subtitle}
				</p>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{benefits.items.map((item, i) => {
						const Icon = getIcon(item.icon);
						return (
							<div
								key={item.title}
								className="flex items-start gap-5 p-8 rounded-xl bg-surface border border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-up"
								style={{ animationDelay: `${i * 0.12}s` }}
							>
								<div className="rounded-full bg-primary/10 p-3 shrink-0">
									<Icon size={24} className="text-primary" />
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2 text-text">{item.title}</h3>
									<p className="text-text-secondary leading-relaxed">{item.description}</p>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}

/* ------------------------------------------------------------------ */
/*  Quote                                                              */
/* ------------------------------------------------------------------ */

function QuoteSection({ quote }: { quote: string }) {
	return (
		<section
			className="py-20 sm:py-24 px-5 sm:px-8"
			style={{
				backgroundColor: "var(--color-background)",
				background: [
					"linear-gradient(180deg,",
					"var(--color-background) 0%,",
					"color-mix(in srgb, var(--color-primary) 6%, var(--color-background)) 50%,",
					"var(--color-background) 100%)",
				].join(" "),
			}}
		>
			<div className="max-w-2xl mx-auto text-center">
				<Logo size={32} className="mx-auto mb-8 opacity-40" />
				<blockquote className="text-xl md:text-2xl leading-relaxed text-text/70 italic font-light">
					&bdquo;{quote}&ldquo;
				</blockquote>
			</div>
		</section>
	);
}

/* ------------------------------------------------------------------ */
/*  Final CTA                                                          */
/* ------------------------------------------------------------------ */

function CtaSection({ cta }: { cta: NonNullable<typeof brand.landing.cta> }) {
	return (
		<section
			className="py-20 sm:py-24 px-5 sm:px-8"
			style={{
				backgroundColor: "var(--color-background)",
				background: [
					"linear-gradient(180deg,",
					"var(--color-background) 0%,",
					"color-mix(in srgb, var(--color-primary) 5%, var(--color-background)) 50%,",
					"color-mix(in srgb, var(--color-primary) 8%, var(--color-background)) 100%)",
				].join(" "),
			}}
		>
			<div className="max-w-xl mx-auto text-center">
				<h2 className="text-3xl md:text-4xl font-bold mb-6 text-text">{cta.title}</h2>
				<p className="text-text-secondary mb-10">{cta.subtitle}</p>
				<Link to="/register" className="no-underline">
					<Button
						variant="primary"
						size="lg"
						className="text-base px-10 py-3 shadow-md hover:shadow-lg transition-shadow font-semibold"
					>
						{cta.buttonText}
					</Button>
				</Link>
			</div>
		</section>
	);
}
